import { useState } from "react";
import { Modal as AntModal, Segmented, Spin } from "antd";
import { DesktopOutlined, MobileOutlined } from "@ant-design/icons";
import { Button } from "@/components/ui";
import dayjs from "dayjs";

interface EmailPreviewData {
  campaignName: string;
  subjectLine: string;
  senderName: string;
  senderEmail: string;
  recipientCount: number;
  htmlContent: string;
  updatedAt?: string;
}

interface EmailPreviewModalProps {
  open: boolean;
  onClose: () => void;
  email: EmailPreviewData;
  mode?: "view" | "create";
  previewRecipient?: {
    name: string;
    email: string;
    source?: "selected" | "recipient" | "latest" | "sample";
  };
  loading?: boolean;
  onCreateEmail?: () => void;
  onSendTest?: () => void;
}

const EmailPreviewModal = ({
  open,
  onClose,
  email,
  mode = "view",
  previewRecipient,
  loading = false,
  onCreateEmail,
  onSendTest,
}: EmailPreviewModalProps) => {
  const [viewMode, setViewMode] = useState<"desktop" | "mobile">("desktop");

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return dayjs(dateString).format("YYYY/MM/DD, hh:mmA");
  };

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}
      centered
      title={
        <div className="flex items-center gap-2">
          <span>Preview Email</span>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
            Draft
          </span>
        </div>
      }
      className="email-preview-modal"
    >
      <div className="flex flex-col">
        {/* View Mode Toggle */}
        <div className="flex justify-center py-4">
          <Segmented
            value={viewMode}
            onChange={(value) => setViewMode(value as "desktop" | "mobile")}
            options={[
              {
                label: (
                  <div className="flex items-center gap-2 px-2">
                    <DesktopOutlined />
                    <span>Desktop</span>
                  </div>
                ),
                value: "desktop",
              },
              {
                label: (
                  <div className="flex items-center gap-2 px-2">
                    <MobileOutlined />
                    <span>Mobile</span>
                  </div>
                ),
                value: "mobile",
              },
            ]}
          />
        </div>

        {/* Email Metadata */}
        <div className="flex items-center gap-4 py-4 px-6 bg-gray-50 rounded-lg mb-4">
          <div className="w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center">
            <span className="text-white text-lg font-medium">
              {email.senderName?.charAt(0) || "A"}
            </span>
          </div>
          <div className="flex-1 grid grid-cols-5 gap-4 text-sm">
            <div>
              <p className="text-gray-500 text-xs mb-1">Campaign Name</p>
              <p className="font-medium text-gray-900 truncate">{email.campaignName}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs mb-1">Sender Name</p>
              <p className="font-medium text-gray-900 truncate">{email.senderName}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs mb-1">Sender Email</p>
              <p className="font-medium text-gray-900 truncate">{email.senderEmail}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs mb-1">Email Subject Line</p>
              <p className="font-medium text-gray-900 truncate">{email.subjectLine}</p>
            </div>
            <div className="flex gap-4">
              {/* <div>
                <p className="text-gray-500 text-xs mb-1">No. of Recipients</p>
                <p className="font-medium text-gray-900">{email.recipientCount}</p>
              </div> */}
              <div>
                <p className="text-gray-500 text-xs mb-1">Last Updated</p>
                <p className="font-medium text-gray-900">{formatDate(email.updatedAt)}</p>
              </div>
            </div>
          </div>
        </div>
        {previewRecipient && (
          <div className="text-xs text-gray-500 mb-4 px-1">
            Previewing as{" "}
            <span className="font-medium text-gray-700">
              {previewRecipient.name || "Attendee"}
            </span>{" "}
            ({previewRecipient.email || "unknown"}){" "}
            {previewRecipient.source === "sample" && (
              <span className="text-orange-500">• sample data</span>
            )}
          </div>
        )}

        {/* Email Preview */}
        <div className="flex justify-center bg-gray-100 p-6 rounded-lg">
          <div
            className={`bg-white shadow-lg transition-all duration-300 ${
              viewMode === "mobile" ? "w-[375px]" : "w-full max-w-[600px]"
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center h-[500px]">
                <Spin />
              </div>
            ) : (
              <iframe
                srcDoc={email.htmlContent}
                title="Email Preview"
                className="w-full h-[500px] border-0"
                sandbox="allow-same-origin"
              />
            )}
          </div>
        </div>

        {/* Footer Actions */}
        {mode === "create" && (
          <div className="flex justify-start gap-3 pt-4 mt-4 border-t">
            <Button variant="primary" onClick={onCreateEmail}>
              Create Email
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Preview
            </Button>
            <Button variant="secondary" onClick={onSendTest}>
              Send Test Email
            </Button>
          </div>
        )}
      </div>
    </AntModal>
  );
};

export default EmailPreviewModal;
